// CGL announcement bar: arrows, auto-advance (Sliding and Rotating), the
// marquee loop and dismiss. The messages are plain HTML, so without this
// script they still work as a swipeable row.
//
// Auto-advance pauses while the mouse is over the bar, while a control or
// link in it has keyboard focus, during a touch, while one of its messages is
// selected in the Theme Editor, while the page is in a background tab, and
// when the shopper presses pause. It resumes once every one of those has
// cleared. With reduced motion turned on, messages still change on the timer
// but without sliding or fading, and the marquee stands still.
//
// The Theme Editor swaps in new section HTML when a setting changes. The old
// element tears itself down when it leaves the page and the new one sets
// itself up, so each bar only ever has one timer and one set of listeners.
if (!customElements.get('cgl-announcement-bar')) {
  customElements.define(
    'cgl-announcement-bar',
    class CglAnnouncementBar extends HTMLElement {
      connectedCallback() {
        this.setUp();
      }

      disconnectedCallback() {
        this.tearDown();
      }

      setUp() {
        if (this.cleanups) return;
        this.cleanups = [];
        this.pauses = new Set();
        this.mode = this.dataset.mode;
        this.track = this.querySelector('[data-track]');
        this.viewport = this.querySelector('.cgl-ab__viewport');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 5000);
        this.index = 0;
        this.visible = [];

        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');
        this.pauseButton = this.querySelector('[data-pause]');

        // Rebuild if the shopper changes their motion setting while here.
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reduceMotion = motion.matches;
        this.listen(motion, 'change', () => {
          this.tearDown();
          this.setUp();
        });

        this.setUpDismiss();
        if (this.mode === 'marquee') {
          this.setUpMarquee();
        } else if (this.hasAttribute('data-multi')) {
          this.setUpCarousel();
        }
        this.listen(document, 'shopify:block:select', (event) => this.onBlockSelect(event, true));
        this.listen(document, 'shopify:block:deselect', (event) => this.onBlockSelect(event, false));
      }

      tearDown() {
        if (!this.cleanups) return;
        this.stop();
        this.cleanups.forEach((cleanup) => cleanup());
        this.cleanups = null;
      }

      listen(target, type, handler, options) {
        if (!target) return;
        target.addEventListener(type, handler, options);
        this.cleanups.push(() => target.removeEventListener(type, handler, options));
      }

      // Dismiss ------------------------------------------------------------
      setUpDismiss() {
        const button = this.querySelector('[data-dismiss]');
        const key = this.dataset.dismissKey;
        if (!button || !key) return;
        button.hidden = false;
        this.listen(button, 'click', () => {
          try {
            localStorage.setItem(key, this.dataset.signature);
          } catch (error) {
            // Storage blocked: close for this page view only.
          }
          this.stop();
          this.hidden = true;
        });
      }

      // Carousel: static, slide and fade ------------------------------------
      setUpCarousel() {
        this.autoplay = this.mode === 'slide' || this.mode === 'fade';
        if (this.mode === 'fade') {
          this.setAttribute('data-ready', '');
          this.cleanups.push(() => this.removeAttribute('data-ready'));
        }
        this.listen(this.prevButton, 'click', () => this.go(this.index - 1, true));
        this.listen(this.nextButton, 'click', () => this.go(this.index + 1, true));
        if (this.mode !== 'fade') {
          this.listen(this.track, 'scroll', () => this.onScroll(), { passive: true });
          this.cleanups.push(() => {
            clearTimeout(this.settleTimer);
            if (this.loopClone) this.loopClone.remove();
            this.loopClone = null;
          });
        }

        // Messages can be desktop-only or mobile-only, so the set of visible
        // messages changes at the breakpoint.
        this.listen(window.matchMedia('(min-width: 750px)'), 'change', () => this.refresh());

        if (this.autoplay) {
          this.listen(this.pauseButton, 'click', () => this.togglePause());
          // Mouse hover only: a tap fires compatibility mouse events, and on a
          // phone the "mouse" never leaves, which used to stop the bar for good.
          this.listen(this, 'pointerenter', (event) => event.pointerType === 'mouse' && this.pause('hover'));
          this.listen(this, 'pointerleave', (event) => event.pointerType === 'mouse' && this.resume('hover'));
          // A touch holds the message while the finger is down, then the
          // timer starts again from zero.
          this.listen(this, 'pointerdown', (event) => event.pointerType !== 'mouse' && this.pause('touch'));
          this.listen(this, 'pointerup', (event) => event.pointerType !== 'mouse' && this.resume('touch'));
          this.listen(this, 'pointercancel', (event) => event.pointerType !== 'mouse' && this.resume('touch'));
          // Keyboard focus, not the focus a click or tap leaves on a button.
          this.listen(this, 'focusin', (event) => this.isKeyboardFocus(event.target) && this.pause('focus'));
          this.listen(this, 'focusout', (event) => {
            if (!this.contains(event.relatedTarget)) this.resume('focus');
          });
          this.listen(document, 'visibilitychange', () =>
            document.hidden ? this.pause('hidden') : this.resume('hidden')
          );
          if (document.hidden) this.pauses.add('hidden');
        }
        this.refresh();
      }

      isKeyboardFocus(element) {
        try {
          return element.matches(':focus-visible');
        } catch (error) {
          return true;
        }
      }

      refresh() {
        this.visible = this.slides.filter((slide) => slide.getClientRects().length > 0);
        const multiple = this.visible.length > 1;
        [this.prevButton, this.nextButton].forEach((button) => button && (button.hidden = !multiple));
        if (this.pauseButton) this.pauseButton.hidden = !(multiple && this.autoplay);

        const template = this.dataset.labelPosition || '';
        this.visible.forEach((slide, i) => {
          if (multiple) {
            slide.setAttribute('role', 'group');
            slide.setAttribute('aria-roledescription', this.dataset.labelSlide || 'slide');
            slide.setAttribute('aria-label', template.replace('__i__', i + 1).replace('__n__', this.visible.length));
          } else {
            ['role', 'aria-roledescription', 'aria-label'].forEach((name) => slide.removeAttribute(name));
          }
        });

        if (this.mode !== 'fade') this.makeLoopClone(multiple);
        this.index = 0;
        this.show(0, false);
        this.restart();
      }

      // A copy of the first message after the last one, so the loop carries on
      // forwards instead of sliding back past every message. Once the bar
      // comes to rest on the copy, it jumps (invisibly) to the real first one.
      makeLoopClone(multiple) {
        if (this.loopClone) this.loopClone.remove();
        this.loopClone = null;
        if (!multiple) return;
        const clone = this.visible[0].cloneNode(true);
        ['data-slide', 'data-block-id', 'data-shopify-editor-block', 'role', 'aria-roledescription', 'aria-label', 'id'].forEach(
          (name) => clone.removeAttribute(name)
        );
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        this.track.appendChild(clone);
        this.loopClone = clone;
      }

      go(index, fromUser) {
        const count = this.visible.length;
        if (count < 2) return;
        const wrapsForward = index === count;
        this.index = (index + count) % count;
        // Announce changes the shopper asked for, not automatic ones.
        this.track.setAttribute('aria-live', fromUser ? 'polite' : 'off');
        if (wrapsForward && this.loopClone && !this.reduceMotion) {
          this.scrollToSlide(this.loopClone, true);
        } else {
          this.show(this.index, true);
        }
        // After an arrow press, give the new message a full turn.
        if (fromUser) this.restart();
      }

      show(index, animate) {
        const target = this.visible[index];
        if (!target) return;
        if (this.mode === 'fade') {
          this.slides.forEach((slide) => slide.toggleAttribute('data-active', slide === target));
          return;
        }
        this.scrollToSlide(target, animate);
      }

      // Measured from the boxes on screen, so it doesn't matter which
      // ancestor the theme happens to position.
      scrollToSlide(slide, animate) {
        const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
        this.track.scrollTo({ left, behavior: animate && !this.reduceMotion ? 'smooth' : 'auto' });
      }

      onScroll() {
        this.syncIndex();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 150);
      }

      settle() {
        if (!this.loopClone) return;
        const origin = this.track.getBoundingClientRect().left;
        if (Math.abs(this.loopClone.getBoundingClientRect().left - origin) < 2) {
          this.index = 0;
          this.scrollToSlide(this.visible[0], false);
        }
      }

      syncIndex() {
        const origin = this.track.getBoundingClientRect().left;
        const candidates = this.loopClone ? [...this.visible, this.loopClone] : this.visible;
        let nearest = 0;
        let distance = Infinity;
        candidates.forEach((slide, i) => {
          const gap = Math.abs(slide.getBoundingClientRect().left - origin);
          if (gap < distance) {
            distance = gap;
            nearest = i;
          }
        });
        this.index = nearest % this.visible.length;
      }

      // Timer -----------------------------------------------------------------
      start() {
        if (!this.autoplay || this.timer || this.pauses.size || this.hidden) return;
        if (this.visible.length < 2) return;
        this.track.setAttribute('aria-live', 'off');
        this.timer = setInterval(() => this.go(this.index + 1, false), this.interval);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }

      restart() {
        this.stop();
        this.start();
      }

      pause(reason) {
        this.pauses.add(reason);
        this.stop();
      }

      resume(reason) {
        this.pauses.delete(reason);
        this.start();
      }

      togglePause() {
        const paused = !this.hasAttribute('data-paused');
        this.toggleAttribute('data-paused', paused);
        this.pauseButton.setAttribute(
          'aria-label',
          paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
        );
        if (this.mode === 'marquee') return;
        paused ? this.pause('user') : this.resume('user');
      }

      // Marquee ---------------------------------------------------------------
      setUpMarquee() {
        if (this.reduceMotion) return;
        this.speed = parseInt(this.dataset.speed, 10) || 50;
        const strip = document.createElement('div');
        strip.className = 'cgl-ab__marquee';
        this.viewport.appendChild(strip);
        strip.appendChild(this.track);
        this.strip = strip;
        this.buildMarquee();

        if (this.pauseButton) {
          this.pauseButton.hidden = false;
          this.listen(this.pauseButton, 'click', () => this.togglePause());
        }
        let width = this.viewport.clientWidth;
        const observer = new ResizeObserver(() => {
          if (Math.abs(this.viewport.clientWidth - width) < 2) return;
          width = this.viewport.clientWidth;
          this.buildMarquee();
        });
        observer.observe(this.viewport);
        // Put the markup back as Liquid rendered it.
        this.cleanups.push(() => {
          observer.disconnect();
          this.viewport.appendChild(this.track);
          strip.remove();
          this.strip = null;
          this.removeAttribute('data-ready');
          this.removeAttribute('data-paused');
          if (this.pauseButton) this.pauseButton.hidden = true;
        });
      }

      // Two identical halves, each at least as wide as the bar, scroll by 50%.
      buildMarquee() {
        this.strip.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
        this.removeAttribute('data-ready');
        const unit = this.track.getBoundingClientRect().width;
        if (!unit) return;
        const perHalf = Math.max(1, Math.ceil(this.viewport.clientWidth / unit));
        for (let i = 1; i < perHalf * 2; i++) {
          const clone = this.track.cloneNode(true);
          clone.removeAttribute('id');
          clone.removeAttribute('data-track');
          clone.setAttribute('data-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          clone.querySelectorAll('[data-slide]').forEach((slide) => {
            slide.removeAttribute('data-slide');
            slide.removeAttribute('data-shopify-editor-block');
          });
          this.strip.appendChild(clone);
        }
        this.strip.style.setProperty('--cgl-ab-duration', `${Math.round((unit * perHalf) / this.speed)}s`);
        this.setAttribute('data-ready', '');
      }

      // Theme Editor: show the selected message and hold still -----------------
      onBlockSelect(event, selected) {
        const slide = this.slides.find((item) => item.dataset.blockId === event.detail.blockId);
        if (!slide) return;
        if (this.mode === 'marquee') {
          this.toggleAttribute('data-paused', selected);
          return;
        }
        if (!selected) {
          this.resume('editor');
          return;
        }
        this.pause('editor');
        const index = this.visible.indexOf(slide);
        if (index > -1) {
          this.index = index;
          this.show(index, false);
        }
      }
    }
  );
}
